import React, { useState, useEffect } from 'react';
import { fetchAuditLogs } from '../utils/audit';
import { SearchIcon, DownloadIcon } from './Icons';

const PAGE_SIZE = 50;

const ACTION_LABELS = {
  'yard_sale.bid_won': { label: 'Bid Won (Sold)', color: 'var(--green)' },
  'yard_sale.approval_requested': { label: 'Sale Approval Requested', color: 'var(--yellow)' },
  'yard_sale.final_bids_requested': { label: 'Final Bids Requested', color: 'var(--blue)' },
  'yard_sale.bids_cancelled': { label: 'Bids Cancelled', color: 'var(--red)' },
  'permission_denied': { label: 'Permission Denied', color: 'var(--red)' },
};

function formatDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' +
    d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export default function AuditLog({ user }) {
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const isAdmin = user.role === 'head_admin' || user.role === 'admin';

  const loadLogs = async () => {
    setLoading(true);
    const filters = {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    };
    if (actionFilter) filters.action = actionFilter;
    if (entityFilter) filters.entityType = entityFilter;
    if (userFilter) filters.userId = userFilter;
    if (dateFrom) filters.dateFrom = new Date(dateFrom).toISOString();
    if (dateTo) filters.dateTo = new Date(dateTo + 'T23:59:59').toISOString();

    const result = await fetchAuditLogs(filters);
    setLogs(result.data);
    setTotalCount(result.count);
    setLoading(false);
  };

  useEffect(() => { loadLogs(); }, [page, actionFilter, entityFilter, userFilter, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Export CSV
  const exportCSV = async () => {
    const all = await fetchAuditLogs({ ...( actionFilter ? { action: actionFilter } : {}), limit: 5000 });
    const rows = (all.data || []).map(l => ({
      Timestamp: formatDate(l.created_at),
      User: l.user_name,
      Role: l.user_role,
      Action: l.action,
      Entity: `${l.entity_type || ''} ${l.entity_id || ''}`.trim(),
      Label: l.entity_label || '',
      Details: JSON.stringify(l.details || {}),
    }));
    const headers = Object.keys(rows[0] || {});
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${String(r[h] || '').replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  if (!isAdmin) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
        <div style={{ fontSize: 14 }}>You do not have permission to view audit logs.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0 }}>Audit Log</h2>
        <button className="btn btn-secondary btn-sm" onClick={exportCSV}><DownloadIcon /> Export CSV</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="inv-filter-select" value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(0); }}>
          <option value="">All Actions</option>
          {Object.entries(ACTION_LABELS).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>
        <select className="inv-filter-select" value={entityFilter} onChange={e => { setEntityFilter(e.target.value); setPage(0); }}>
          <option value="">All Entities</option>
          <option value="yard_bid">Yard Bids</option>
          <option value="unit">Units</option>
          <option value="invoice">Invoices</option>
          <option value="payment">Payments</option>
          <option value="container">Containers</option>
          <option value="cut_plan">Cut Plans</option>
        </select>
        <input type="date" className="form-input" style={{ width: 140, fontSize: 11, padding: '4px 8px' }} value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }} title="From date" />
        <input type="date" className="form-input" style={{ width: 140, fontSize: 11, padding: '4px 8px' }} value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }} title="To date" />
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>{totalCount} entries</span>
      </div>

      {/* Log table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Loading...</div>
      ) : logs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
          <div style={{ fontSize: 14 }}>No audit entries found</div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ fontSize: 12, width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ minWidth: 130 }}>Timestamp</th>
                <th style={{ minWidth: 100 }}>User</th>
                <th style={{ minWidth: 60 }}>Role</th>
                <th style={{ minWidth: 140 }}>Action</th>
                <th style={{ minWidth: 180 }}>Entity</th>
                <th style={{ minWidth: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const actionInfo = ACTION_LABELS[log.action] || { label: log.action, color: 'var(--text3)' };
                const isExpanded = expandedId === log.id;
                return (
                  <React.Fragment key={log.id}>
                    <tr style={{ cursor: 'pointer', background: isExpanded ? 'rgba(88,166,255,0.04)' : undefined }}
                      onClick={() => setExpandedId(isExpanded ? null : log.id)}>
                      <td style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text2)' }}>{formatDate(log.created_at)}</td>
                      <td style={{ fontWeight: 600 }}>{log.user_name || '—'}</td>
                      <td style={{ fontSize: 10, color: 'var(--text3)' }}>{(log.user_role || '').replace(/_/g, ' ')}</td>
                      <td>
                        <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: `${actionInfo.color}15`, color: actionInfo.color }}>
                          {actionInfo.label}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: 11 }}>{log.entity_label || '—'}</div>
                        {log.entity_type && <div style={{ fontSize: 9, color: 'var(--text3)', fontFamily: 'monospace' }}>{log.entity_type} · {(log.entity_id || '').substring(0, 12)}</div>}
                      </td>
                      <td style={{ textAlign: 'center', fontSize: 10, color: 'var(--text3)' }}>{isExpanded ? '▲' : '▼'}</td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={6} style={{ padding: '12px 16px', background: 'var(--bg3)' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6 }}>Full Details</div>
                          <pre style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--text2)', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, maxHeight: 200, overflowY: 'auto', padding: 10, background: 'var(--bg1)', borderRadius: 6 }}>
                            {JSON.stringify(log.details || {}, null, 2)}
                          </pre>
                          {log.user_agent && (
                            <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 6 }}>
                              UA: {log.user_agent.substring(0, 100)}...
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16, alignItems: 'center' }}>
          <button className="btn btn-secondary btn-sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>Page {page + 1} of {totalPages}</span>
          <button className="btn btn-secondary btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}
